import React from "react";
import { CourseFilter, FilterOptions } from "../../services/api/courseService";
import Dropdown, { DropdownOption } from "./Dropdown";

interface CourseFiltersProps {
  currentFilters: CourseFilter;
  availableFilters: FilterOptions | null;
  hasFilters: boolean;
  onFilterChange: (key: keyof CourseFilter, value: string | number) => void;
  onClearFilters: () => void;
}

const CourseFilters: React.FC<CourseFiltersProps> = ({
  currentFilters,
  availableFilters,
  hasFilters,
  onFilterChange,
  onClearFilters,
}) => {
  // Convert filter data to dropdown options
  const levelOptions: DropdownOption[] = [
    { value: "", label: "All Levels" },
    ...(availableFilters?.levels?.map((level) => ({
      value: level.level,
      label: level.level.replace(/_/g, " "),
      count: level.count,
    })) || []),
  ];

  const universityOptions: DropdownOption[] = [
    { value: "", label: "All Universities" },
    ...(availableFilters?.universities?.map((uni) => ({
      value: uni.id,
      label: uni.name,
      count: uni.count,
    })) || []),
  ];

  const studyModeOptions: DropdownOption[] = [
    { value: "", label: "All Study Modes" },
    ...(availableFilters?.studyModes?.map((mode) => ({
      value: mode.studyMode,
      label: mode.studyMode.replace(/_/g, " "),
      count: mode.count,
    })) || []),
  ];

  const languageOptions: DropdownOption[] = [
    { value: "", label: "All Languages" },
    { value: "English", label: "English" },
    { value: "Korean", label: "Korean" },
    { value: "Both", label: "Both" },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        {hasFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-cyan-600 hover:text-cyan-700 font-medium transition-colors duration-200"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Level Filter */}
        {availableFilters?.levels && availableFilters.levels.length > 0 && (
          <Dropdown
            label="Degree Level"
            options={levelOptions}
            value={currentFilters.level || ""}
            onChange={(value) => onFilterChange("level", value)}
            searchable={levelOptions.length > 5}
            showCount={true}
            className="w-full"
          />
        )}

        {/* University Filter */}
        {availableFilters?.universities &&
          availableFilters.universities.length > 0 && (
            <Dropdown
              label="University"
              options={universityOptions}
              value={currentFilters.universityId || ""}
              onChange={(value) => onFilterChange("universityId", value)}
              searchable={universityOptions.length > 5}
              showCount={true}
              className="w-full"
            />
          )}

        {/* Study Mode Filter */}
        {availableFilters?.studyModes &&
          availableFilters.studyModes.length > 0 && (
            <Dropdown
              label="Study Mode"
              options={studyModeOptions}
              value={currentFilters.studyMode || ""}
              onChange={(value) => onFilterChange("studyMode", value)}
              showCount={true}
              className="w-full"
            />
          )}

        {/* Language Filter */}
        <Dropdown
          label="Language"
          options={languageOptions}
          value={currentFilters.language || ""}
          onChange={(value) => onFilterChange("language", value)}
          className="w-full"
        />

        {/* Fee Range Filter */}
        {availableFilters?.feeRange && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Annual Fee Range (USD)
            </label>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="number"
                  placeholder="Min Fee"
                  value={currentFilters.minFee || ""}
                  onChange={(e) =>
                    onFilterChange("minFee", Number(e.target.value) || "")
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all duration-200"
                  min={availableFilters.feeRange.min}
                  max={availableFilters.feeRange.max}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">USD</span>
                </div>
              </div>
              <div className="relative">
                <input
                  type="number"
                  placeholder="Max Fee"
                  value={currentFilters.maxFee || ""}
                  onChange={(e) =>
                    onFilterChange("maxFee", Number(e.target.value) || "")
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all duration-200"
                  min={availableFilters.feeRange.min}
                  max={availableFilters.feeRange.max}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">USD</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 flex items-center">
              <svg
                className="h-3 w-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Range: ${availableFilters.feeRange.min?.toLocaleString()} - $
              {availableFilters.feeRange.max?.toLocaleString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseFilters;
